import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { DraftStateSlice } from '@/features/jobs/drafting/DraftJobViewModel'
import type {
  StepAccessor,
  StepFactory,
} from '@/features/jobs/drafting/OutlineViewModel'
import type { PreviousProviderStore } from '@/features/jobs/drafting/PreviousProviderStore'
import type { DraftJob } from '@/features/jobs/drafting/DraftJob'
import { action, computed, observable, runInAction } from 'mobx'
import { ChampionProviderSelection } from '@/features/jobs/drafting/DraftJobStore'
import { Id } from '@/infra/id'
import type { PreviousProvider } from '@/features/jobs/drafting/PreviousProvider'
import { Subject } from 'rxjs'
import type { DraftChange } from '@/features/jobs/drafting/DraftPersister'

/**
 * View-model for selecting a previous Provider.
 */
export class RequestedProviderSelectionViewModel
  implements ActivationLifecycle, DraftStateSlice, StepAccessor
{
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _changesSubject = new Subject<DraftChange>()
  private readonly _continueSubject = new Subject<void>()

  /**
   * Emits changes to persist.
   */
  readonly changes$ = this._changesSubject.asObservable()

  /**
   * The step VM.
   */
  readonly step

  /**
   * The current selection.
   */
  @observable accessor selection: ChampionProviderSelection | null = null

  /**
   * List of previous Providers.
   */
  @observable accessor previousProviders: PreviousProvider[] = []

  /**
   * Emits when we want to trigger the "continue" action.
   */
  readonly continue$ = this._continueSubject.asObservable()

  constructor(
    private readonly _job: DraftJob,
    private readonly _previousProviderStore: PreviousProviderStore,
    private readonly _isReadOnly: () => boolean,
    createStep: StepFactory,
  ) {
    this.step = createStep({
      stepId: 'requested_provider',
      isCompleted: () => this.isCompleted,
      isShortCircuited: () => false,
      outlineItemDisplay: () => ({
        title: 'Would you like to reconnect with a previous Pro?',
      }),
    })
  }

  /**
   * Whether we're in read-only mode.
   */
  @computed get isReadOnly() {
    return this._isReadOnly()
  }

  /**
   * Whether the step has been completed.
   */
  @computed get isCompleted() {
    return !!this.selection
  }

  /**
   * Whether Provider selection is allowed.
   */
  @computed get isSelectionAllowed() {
    return this._job.legend.isRequestedProviderSelectionAllowed
  }

  /** @inheritDoc */
  @computed get stepAccessors(): StepAccessor[] {
    if (!this.isSelectionAllowed) {
      return []
    }

    if (this.previousProviders.length === 0) {
      return []
    }

    return [this]
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /** @inheritDoc */
  async prepare(signal?: AbortSignal) {
    if (!this.isSelectionAllowed) {
      return
    }

    const providers =
      await this._previousProviderStore.listPreviousProviders(signal)

    // There's no way for us to persist the "any" selection, so
    // if no Provider has been requested, we'll have to assume the user
    // has not made a choice.
    let selection: ChampionProviderSelection | null
    if (this._job.requestedProviderUserId === null) {
      selection = null
    } else {
      // Make sure whichever Provider which was selected is still
      // present in the list.
      const isProviderListed = providers.some(
        (p) => p.userId === this._job.requestedProviderUserId,
      )

      if (!isProviderListed) {
        selection = null
      } else {
        selection = ChampionProviderSelection.specificChampionProvider(
          Id.assert(this._job.requestedProviderUserId),
        )
      }
    }

    runInAction(() => {
      this.selection = selection
      this.previousProviders = providers
    })
  }

  /**
   * Selects a specific provider.
   *
   * @param provider
   */
  @action.bound
  selectProvider(provider: PreviousProvider) {
    if (this.isReadOnly) {
      return
    }

    this.selection = ChampionProviderSelection.specificChampionProvider(
      provider.userId,
    )
    this._changesSubject.next({
      type: 'SetRequestedProvider',
      selection: {
        type: 'SpecificChampionProvider',
        providerUserId: provider.userId,
      },
    })
    this._continueSubject.next()
  }

  /**
   * Selects any Provider.
   */
  @action.bound
  selectAnyProvider() {
    if (this.isReadOnly) {
      return
    }

    this.selection = ChampionProviderSelection.anyChampionProvider()
    this._changesSubject.next({
      type: 'SetRequestedProvider',
      selection: { type: 'AnyChampionProvider' },
    })
    this._continueSubject.next()
  }
}

/**
 * Factory for creating a {@link RequestedProviderSelectionViewModel}.
 */
export type RequestedProviderSelectionViewModelFactory = ReturnType<
  typeof createRequestedProviderSelectionViewModelFactory
>

/** @see RequestedProviderSelectionViewModelFactory */
export function createRequestedProviderSelectionViewModelFactory(
  job: DraftJob,
  previousProviderStore: PreviousProviderStore,
) {
  return (isReadOnly: () => boolean, createStep: StepFactory) =>
    new RequestedProviderSelectionViewModel(
      job,
      previousProviderStore,
      isReadOnly,
      createStep,
    )
}
